import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronRight, Eye, Globe, Heart, LogOut, Minimize2, Maximize2, Monitor,
  Pin, PinOff, Search, Smartphone, Star, Ticket, UserRound, X,
} from 'lucide-react';
import { EmojiTile, GlyphTile } from '../../screens/CMS/Layout/HomepageSettings/components/TravelIconPicker';
import { isGlyph, useTravelIcons } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import { formatDMY } from '../../utils/dateFormat';
import { resolveLogoUrl } from './LogoConfigSection';
import { announcementStatus, brusselsToday, hasText } from './siteHeaderModel';
import {
  ACCOUNT_MENU_DEFAULTS, ANNOUNCEMENT_TONE_STYLES, SERVICE_DEFAULTS, WEBSITE_DEFAULTS, pickText, tally,
} from './headerDefaults';
import { CUSTOM_ROW_ICON, MENU_BUTTON_ICONS, SERVICE_ICONS, supportIcon } from './headerIcons';
import { previewMenus, showsOn } from './headerPreviewModel';
import { Segmented } from './editorControls';
import styles from './HeaderPreview.module.css';

// A live drawing of the website header with the settings as they stand, unsaved edits
// included. It mirrors sunsky-website's Navbar: desktop is the bar (menus beside the logo, the
// search in the middle, support, reviews, language and account on the right); mobile is the
// bar with logo, support and the menu button, plus the drawer it opens. Every block honours
// its on/off switch and its Show on choice. The announcement strip is drawn even outside its
// dates, marked as such, so it can be checked before it goes live.

// The desktop bar is laid out at a wide-screen width (wider still when its contents need it,
// e.g. with every custom menu on) and scaled down to fit the dock; the dropdown panels open
// below it at full size so their text stays readable.
const DESIGN_WIDTH = 1360;
// The bar's padding and gaps, plus the room the search ticket keeps for itself.
const BAR_CHROME = 80 + 48;
const SEARCH_ROOM = 320;

const ACCOUNT_ICONS = { bookings: Ticket, profile: UserRound, favourites: Heart };

function Logo({ url, alt, compact = false }) {
  const [aspect, setAspect] = useState(null);
  const [failed, setFailed] = useState(false);
  const src = resolveLogoUrl(url);
  useEffect(() => setFailed(false), [src]);
  // A wide wordmark carries the name; a square mark gets "SunSky" beside it, as on the site.
  // With no uploaded logo the website draws its own bundled one, which this app does not ship,
  // so the name stands in for it.
  const wordmark = aspect !== null && aspect > 1.6 && !failed;
  return (
    <span className={`${styles.logo} ${compact ? styles.logoCompact : ''}`}>
      {src && !failed && (
        <img
          src={src}
          alt={alt || 'Logo'}
          className={wordmark ? styles.logoWordmark : styles.logoMark}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setAspect(w / h);
          }}
          onError={() => setFailed(true)}
        />
      )}
      {!wordmark && (
        <span className={styles.logoText}>Sun<span>Sky</span></span>
      )}
    </span>
  );
}

/** A row's icon: the chosen emoji, or the website's own icon in its tile. */
function RowIcon({ row, icons, size = 34 }) {
  if (row.icon) return <EmojiTile name={row.icon} icons={icons} size={size} />;
  const Icon = row.builtIn || CUSTOM_ROW_ICON;
  return (
    <span className={styles.rowIcon} style={{ width: size, height: size }}>
      <Icon size={Math.round(size * 0.45)} />
    </span>
  );
}

function MenuButtonIcon({ entry, icons }) {
  if (entry.kind === 'custom' && isGlyph(entry.menu.glyph)) {
    return <GlyphTile name={entry.menu.glyph} tone="blue" icons={icons} size={18} />;
  }
  const Icon = MENU_BUTTON_ICONS[entry.kind] || MENU_BUTTON_ICONS.custom;
  return <Icon size={15} />;
}

function MenuPanel({ entry, lang, icons }) {
  return (
    <div className={styles.panel} role="menu" aria-label={`${entry.label} menu`}>
      <div className={styles.panelStrip}>{tally(entry.kind, entry.rows.length, lang)}</div>
      {entry.rows.map((row) => (
        <div key={row.key} className={styles.panelRow} title={row.url || ''}>
          <RowIcon row={row} icons={icons} />
          <span className={styles.panelText}>
            <strong>{row.label || <em className={styles.missing}>No label</em>}</strong>
            {row.sub && <span>{row.sub}</span>}
          </span>
          <ChevronRight size={14} className={styles.panelArrow} />
        </div>
      ))}
      {entry.footer && (
        <div className={styles.panelFooter}>
          {entry.footer}
          <ChevronRight size={14} />
        </div>
      )}
    </div>
  );
}

function AnnouncementStrip({ a, lang, status }) {
  const tone = ANNOUNCEMENT_TONE_STYLES[a.tone] || ANNOUNCEMENT_TONE_STYLES.orange;
  const linkText = pickText(a.linkLabel, lang);
  return (
    <div
      className={`${styles.strip} ${status !== 'live' ? styles.stripPreviewOnly : ''}`}
      style={{ background: tone.background, color: tone.color }}
    >
      <span className={styles.stripText}>{pickText(a.text, lang)}</span>
      {linkText && a.link && <span className={styles.stripLink}>{linkText} →</span>}
      {status !== 'live' && (
        <span className={styles.stripBadge}>{status === 'scheduled' ? 'Scheduled' : 'Outside its dates'}</span>
      )}
      {a.dismissible && <X size={14} className={styles.stripClose} />}
    </div>
  );
}

function AccountMenuList({ account, lang, compact }) {
  return (
    <>
      {account.menuItems.filter((m) => m.enabled).map((m) => {
        const Icon = ACCOUNT_ICONS[m.key];
        return (
          <div key={m.key} className={compact ? styles.drawerLink : styles.dropItem}>
            {!compact && <Icon size={14} />}
            {pickText(m.label, lang, ACCOUNT_MENU_DEFAULTS[m.key])}
          </div>
        );
      })}
    </>
  );
}

/** The bar as a desktop visitor sees it. */
function DesktopPreview({ config, sh, menus, lang, visitor, openMenu, setOpenMenu, onLang, icons }) {
  const frameRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const buttonRefs = useRef({});
  const accountRef = useRef(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const [barWidth, setBarWidth] = useState(DESIGN_WIDTH);
  const [panelLeft, setPanelLeft] = useState(0);
  const [accountOpen, setAccountOpen] = useState(false);

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    // Measured once now, so the first paint is already to scale, then on every resize.
    if (el.clientWidth > 0) setFrameWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (w > 0) setFrameWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // How wide the bar must be for everything in it: the two flanks do not shrink, the search
  // takes what is left. Measured again whenever a flank changes size (a label typed, a menu
  // switched on, the logo loaded).
  const measureBar = useCallback(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) return;
    const zoom = Number(left.closest('[data-canvas]')?.style.zoom) || 1;
    const sides = (left.getBoundingClientRect().width + right.getBoundingClientRect().width) / zoom;
    const needed = Math.max(DESIGN_WIDTH, Math.ceil(sides + BAR_CHROME + SEARCH_ROOM));
    setBarWidth((prev) => (Math.abs(needed - prev) > 1 ? needed : prev));
  }, []);

  useLayoutEffect(() => {
    measureBar();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measureBar);
    [leftRef.current, rightRef.current].forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [measureBar]);
  const scale = frameWidth ? Math.min(1, frameWidth / barWidth) : 1;

  // Line the open panel up under its button, kept inside the dock.
  useLayoutEffect(() => {
    const btn = openMenu && buttonRefs.current[openMenu];
    const frame = frameRef.current;
    if (!btn || !frame) return;
    const left = btn.getBoundingClientRect().left - frame.getBoundingClientRect().left;
    setPanelLeft(Math.max(0, Math.min(left, frame.clientWidth - 312)));
  }, [openMenu, scale, menus]);

  const a = sh.announcement;
  const status = announcementStatus(a, brusselsToday());
  const showStrip = a.enabled && hasText(a.text) && showsOn(a.showOn, 'desktop');
  const visibleMenus = menus.filter((m) => m.menu.enabled && showsOn(m.menu.showOn, 'desktop') && m.rows.length > 0);
  const open = visibleMenus.find((m) => m.key === openMenu);
  const search = sh.search.enabled && showsOn(sh.search.showOn, 'desktop');
  const support = sh.support.enabled && showsOn(sh.support.showOn, 'desktop');
  const SupportIcon = supportIcon(sh.support.icon);
  const trustpilot = sh.trustpilot.enabled && showsOn(sh.trustpilot.showOn, 'desktop');
  // The score as the website prints it (Dutch decimal comma), or nothing when none is entered.
  const scoreNum = Number(String(sh.trustpilot.score ?? '').replace(',', '.'));
  const trustScore = sh.trustpilot.score != null && sh.trustpilot.score !== '' && scoreNum >= 1 && scoreNum <= 5
    ? (lang === 'nl' ? scoreNum.toFixed(1).replace('.', ',') : scoreNum.toFixed(1))
    : '';
  const language = sh.language.enabled && showsOn(sh.language.showOn, 'desktop');
  const account = sh.account;
  const guestEntry = account.enabled && showsOn(account.showOn, 'desktop');
  const register = account.register.enabled;
  const memberName = pickText(account.fallbackName, lang, WEBSITE_DEFAULTS.myAccount);

  useEffect(() => {
    if (visitor !== 'member') setAccountOpen(false);
  }, [visitor]);

  return (
    <div className={styles.desktopFrame} ref={frameRef}>
      <div className={styles.canvas} data-canvas style={{ width: barWidth, zoom: scale }}>
        {showStrip && <AnnouncementStrip a={a} lang={lang} status={status} />}
        <div className={styles.bar}>
          <div className={styles.side} ref={leftRef}>
            <Logo url={config.logoUrl} alt={config.logoAltText} />
            {visibleMenus.map((m) => (
              <button
                key={m.key}
                type="button"
                ref={(el) => { buttonRefs.current[m.key] = el; }}
                className={`${styles.menuBtn} ${openMenu === m.key ? styles.menuBtnOpen : ''}`}
                aria-expanded={openMenu === m.key}
                onClick={() => setOpenMenu(openMenu === m.key ? null : m.key)}
              >
                <span className={styles.menuBtnIcon}><MenuButtonIcon entry={m} icons={icons} /></span>
                <span className={styles.menuBtnLabel}>{m.label || <em className={styles.missing}>No label</em>}</span>
                <ChevronDown size={12} className={openMenu === m.key ? styles.chevronUp : ''} />
              </button>
            ))}
          </div>

          {search && (
            <div className={styles.searchWrap}>
              <div className={styles.search}>
                <Search size={16} />
                <span>{pickText(sh.search.placeholder, lang, WEBSITE_DEFAULTS.searchPlaceholder)}</span>
              </div>
            </div>
          )}

          <div className={`${styles.side} ${styles.sideEnd}`} ref={rightRef}>
            {support && (
              <span className={styles.support} title={sh.support.link}>
                <SupportIcon size={18} />
                {sh.support.showLabel && <span>{pickText(sh.support.label, lang, WEBSITE_DEFAULTS.customerService)}</span>}
              </span>
            )}
            {trustpilot && (
              <span className={styles.trustpilot} title="Links to your Trustpilot reviews">
                <Star size={13} /> Trustpilot{trustScore ? ` ${trustScore}` : ''}
              </span>
            )}
            {language && (
              <button type="button" className={styles.lang} onClick={() => onLang(lang === 'nl' ? 'en' : 'nl')} title="Switch the preview language">
                <Globe size={14} /> {lang.toUpperCase()}
              </button>
            )}
            {visitor === 'member' ? (
              <button type="button" ref={accountRef} className={styles.avatarBtn} onClick={() => setAccountOpen((o) => !o)}>
                <span className={styles.avatar}>SK</span>
                <span>{memberName}</span>
                <ChevronDown size={13} />
              </button>
            ) : guestEntry ? (
              <span className={styles.authPill}>
                <span className={styles.authLink}>
                  <UserRound size={14} /> {pickText(account.signInLabel, lang, WEBSITE_DEFAULTS.signIn)}
                </span>
                {register && (
                  <>
                    <span className={styles.authSlash}>/</span>
                    <span className={styles.authLink}>{pickText(account.register.label, lang, WEBSITE_DEFAULTS.register)}</span>
                  </>
                )}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {(open || (visitor === 'member' && accountOpen)) && (
        <div className={styles.panelStage}>
          {open && (
            <div style={{ marginLeft: panelLeft }}>
              <MenuPanel entry={open} lang={lang} icons={icons} />
            </div>
          )}
          {visitor === 'member' && accountOpen && (
            <div className={styles.dropdown}>
              <div className={styles.dropHead}>
                <span className={styles.avatar}>SK</span>
                <span>
                  <strong>{memberName}</strong>
                  <span>visitor@example.com</span>
                </span>
              </div>
              <AccountMenuList account={account} lang={lang} />
              <div className={styles.dropDivider} />
              <div className={`${styles.dropItem} ${styles.dropLogout}`}>
                <LogOut size={14} /> {pickText(null, lang, WEBSITE_DEFAULTS.signOut)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The phone layout: the bar, and the drawer the menu button opens. */
function MobilePreview({ config, sh, menus, lang, visitor, expanded, setExpanded, onLang, icons }) {
  const a = sh.announcement;
  const status = announcementStatus(a, brusselsToday());
  const showStrip = a.enabled && hasText(a.text) && showsOn(a.showOn, 'mobile');
  const support = sh.support.enabled && showsOn(sh.support.showOn, 'mobile');
  const SupportIcon = supportIcon(sh.support.icon);
  const mm = sh.mobileMenu;
  const search = mm.showSearch && sh.search.enabled && showsOn(sh.search.showOn, 'mobile');
  const rows = mm.items.filter((it) => it.active);
  const groups = mm.showMenus
    ? menus.filter((m) => m.menu.enabled && showsOn(m.menu.showOn, 'mobile') && m.rows.length > 0)
    : [];
  const language = sh.language.enabled && showsOn(sh.language.showOn, 'mobile');
  const account = sh.account;
  const guestEntry = account.enabled && showsOn(account.showOn, 'mobile');
  const memberName = pickText(account.fallbackName, lang, WEBSITE_DEFAULTS.myAccount);

  return (
    <div className={styles.phone}>
      <div className={styles.phoneScreen}>
        {showStrip && <AnnouncementStrip a={a} lang={lang} status={status} />}
        <div className={styles.mBar}>
          <Logo url={config.logoUrl} alt={config.logoAltText} compact />
          <div className={styles.mBarEnd}>
            {support && (
              <span className={styles.mIconBtn} title={pickText(sh.support.label, lang, WEBSITE_DEFAULTS.customerService)}>
                <SupportIcon size={18} />
              </span>
            )}
            <span className={styles.mIconBtn} title={pickText(null, lang, WEBSITE_DEFAULTS.menu)}>
              <X size={20} />
            </span>
          </div>
        </div>

        <div className={styles.drawer}>
          {search && (
            <div className={`${styles.search} ${styles.searchMobile}`}>
              <Search size={15} />
              <span>{pickText(sh.search.placeholder, lang, WEBSITE_DEFAULTS.searchPlaceholder)}</span>
            </div>
          )}

          {rows.length > 0 && (
            <div className={styles.drawerRows}>
              {rows.map((it) => {
                const Icon = it.key ? SERVICE_ICONS[it.key] : CUSTOM_ROW_ICON;
                return (
                  <div key={it.id} className={styles.drawerLink} title={it.url}>
                    {it.icon
                      ? <EmojiTile name={it.icon} icons={icons} size={22} />
                      : <Icon size={15} />}
                    {pickText(it.label, lang, it.key ? SERVICE_DEFAULTS[it.key] : null) || <em className={styles.missing}>No label</em>}
                  </div>
                );
              })}
            </div>
          )}

          {groups.map((g) => {
            const isOpen = expanded === g.key;
            return (
              <div key={g.key} className={styles.group}>
                <button type="button" className={styles.groupHead} aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : g.key)}>
                  <span className={styles.menuBtnIcon}><MenuButtonIcon entry={g} icons={icons} /></span>
                  <span className={styles.groupLabel}>{g.label || <em className={styles.missing}>No label</em>}</span>
                  <span className={styles.groupCount}>{g.rows.length}</span>
                  <ChevronDown size={14} className={isOpen ? styles.chevronUp : ''} />
                </button>
                {isOpen && (
                  <div className={styles.groupBody}>
                    {g.rows.map((row) => (
                      <div key={row.key} className={styles.groupRow}>
                        <RowIcon row={row} icons={icons} size={28} />
                        <span className={styles.panelText}>
                          <strong>{row.label || <em className={styles.missing}>No label</em>}</strong>
                          {row.sub && <span>{row.sub}</span>}
                        </span>
                      </div>
                    ))}
                    {g.footer && <div className={styles.panelFooter}>{g.footer} <ChevronRight size={13} /></div>}
                  </div>
                )}
              </div>
            );
          })}

          {language && (
            <div className={styles.mLang}>
              {['nl', 'en'].map((l) => (
                <button key={l} type="button" className={`${styles.mLangBtn} ${lang === l ? styles.mLangOn : ''}`} onClick={() => onLang(l)}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {(visitor === 'member' || guestEntry) && <div className={styles.drawerDivider} />}
          {visitor === 'member' ? (
            <div className={styles.mAccount}>
              <div className={styles.mUser}>
                <span className={styles.avatar}>SK</span>
                <span>
                  <strong>{memberName}</strong>
                  <span>visitor@example.com</span>
                </span>
              </div>
              <AccountMenuList account={account} lang={lang} compact />
              <div className={`${styles.drawerLink} ${styles.dropLogout}`}>{pickText(null, lang, WEBSITE_DEFAULTS.signOut)}</div>
            </div>
          ) : guestEntry ? (
            <div className={styles.mAccount}>
              <span className={styles.mSignIn}>{pickText(account.signInLabel, lang, WEBSITE_DEFAULTS.signIn)}</span>
              {account.register.enabled && (
                <span className={styles.mRegister}>{pickText(account.register.label, lang, WEBSITE_DEFAULTS.register)}</span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Short lines about what the preview cannot show by itself. */
function previewNotes(sh, menus, device) {
  const notes = [];
  const a = sh.announcement;
  const status = announcementStatus(a, brusselsToday());
  if (status === 'scheduled') notes.push(`Announcement bar starts ${formatDMY(a.startDate, '')}: previewed here, visitors do not see it yet.`);
  if (status === 'ended') notes.push(`Announcement bar ended ${formatDMY(a.endDate, '')}: previewed here, visitors no longer see it.`);
  if (status === 'empty') notes.push('Announcement bar is on but has no text, so it does not show.');
  menus.forEach((m) => {
    if (m.menu.enabled && showsOn(m.menu.showOn, device) && m.rows.length === 0) {
      notes.push(`${m.label || 'A menu'} has nothing to list, so the website leaves it out.`);
    }
  });
  // The website draws the score in the wide desktop bar only, never in the phone menu.
  if (device === 'desktop' && sh.trustpilot.enabled && showsOn(sh.trustpilot.showOn, device)) {
    notes.push('Reviews show as a Trustpilot link to your review profile (a small star below 1700px wide).');
  }
  if (device === 'desktop') {
    notes.push('Homepage: the bar floats as a white card under a blue strip that carries the Trustpilot rating, the announcement and the language switch. This preview shows the bar used on every other page.');
  }
  return notes;
}

/**
 * The preview dock. `focus` ({ target, n }) points it at the card being edited: a menu's card
 * opens that menu, the mobile menu card switches to the phone.
 */
export default function HeaderPreview({ config, homepageGroups, holidayTypes, lang, onLang, focus, pinned, onPinned }) {
  const icons = useTravelIcons();
  const sh = config.siteHeader;
  const [device, setDevice] = useState('desktop');
  const [visitor, setVisitor] = useState('guest');
  const [collapsed, setCollapsed] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const menus = useMemo(
    () => previewMenus(sh, { homepageGroups, holidayTypes, lang }),
    [sh, homepageGroups, holidayTypes, lang]
  );

  useEffect(() => {
    const target = focus?.target;
    if (!target) return;
    if (target.startsWith('menu:')) {
      const key = target.slice(5);
      setOpenMenu(key);
      setExpanded(key);
    } else if (target === 'mobile') {
      setDevice('mobile');
    } else if (target === 'account') {
      setOpenMenu(null);
    }
  }, [focus]);

  const notes = previewNotes(sh, menus, device);

  return (
    <div className={`${styles.dock} ${pinned ? styles.dockIsPinned : ''}`}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarTitle}>
          <Eye size={16} />
          <strong>Live preview</strong>
          <span>Unsaved changes included. Click a menu in the bar to open it.</span>
        </div>
        <div className={styles.toolbarControls}>
          <Segmented
            size="sm"
            ariaLabel="Preview device"
            value={device}
            onChange={setDevice}
            options={[
              { value: 'desktop', label: 'Desktop', icon: Monitor },
              { value: 'mobile', label: 'Mobile', icon: Smartphone },
            ]}
          />
          <Segmented
            size="sm"
            ariaLabel="Preview language"
            value={lang}
            onChange={onLang}
            options={[{ value: 'nl', label: 'NL' }, { value: 'en', label: 'EN' }]}
          />
          <Segmented
            size="sm"
            ariaLabel="Preview visitor"
            value={visitor}
            onChange={setVisitor}
            options={[{ value: 'guest', label: 'Guest' }, { value: 'member', label: 'Signed in' }]}
          />
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => onPinned(!pinned)}
            title={pinned ? 'Let the preview scroll away' : 'Keep the preview in view while scrolling'}
            aria-pressed={pinned}
          >
            {pinned ? <PinOff size={14} /> : <Pin size={14} />}
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Show the preview' : 'Hide the preview'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className={`${styles.stage} ${device === 'mobile' ? styles.stageMobile : ''}`}>
          {device === 'desktop' ? (
            <DesktopPreview
              config={config}
              sh={sh}
              menus={menus}
              lang={lang}
              visitor={visitor}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              onLang={onLang}
              icons={icons}
            />
          ) : (
            <MobilePreview
              config={config}
              sh={sh}
              menus={menus}
              lang={lang}
              visitor={visitor}
              expanded={expanded}
              setExpanded={setExpanded}
              onLang={onLang}
              icons={icons}
            />
          )}
          {notes.length > 0 && (
            <ul className={styles.notes}>
              {notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
